import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../contexts/LanguageContext';
import LocalizedLink from '../components/common/LocalizedLink';
import SEO from '../components/common/SEO';
import { articlesEn } from '../data/articles/en/index';
import { articlesZh } from '../data/articles/zh/index';
import { articlesJa } from '../data/articles/ja/index';

const { FiCalendar, FiClock, FiUser, FiArrowLeft, FiShare2, FiTag, FiArrowUp, FiList } = FiIcons;

const BlogArticlePage = () => {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const [readProgress, setReadProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showTOC, setShowTOC] = useState(false);
  const [headings, setHeadings] = useState([]);

  const articles = useMemo(() => {
    switch (language) {
      case 'zh': return articlesZh;
      case 'ja': return articlesJa;
      default: return articlesEn;
    }
  }, [language]);

  const article = useMemo(() => {
    return articles.find(a => a.slug === slug);
  }, [articles, slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter(a => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [articles, article]);

  // Extract headings for Table of Contents
  useEffect(() => {
    if (article) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = article.content;
      const h2Elements = tempDiv.querySelectorAll('h2');
      const extractedHeadings = Array.from(h2Elements).map((h2, index) => ({
        id: `heading-${index}`,
        text: h2.textContent
      }));
      setHeadings(extractedHeadings);
    }
  }, [article]);

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setReadProgress(progress);
      setShowScrollTop(scrolled > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : language === 'zh' ? 'zh-TW' : 'ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href,
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
      setShowTOC(false);
    }
  };

  if (!article) {
    return <Navigate to={`/${language}/blog`} replace />;
  }

  // Add IDs to headings in content
  const contentWithIds = article.content.replace(/<h2>/g, (match, offset) => {
    const index = article.content.substring(0, offset).split('<h2>').length - 1;
    return `<h2 id="heading-${index}">`;
  });

  return (
    <>
      <SEO
        title={`${article.title} - Pet Care Note Blog`}
        description={article.excerpt}
        keywords={article.tags.join(', ')}
        type="article"
        image={article.image}
      />

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
        <motion.div
          className="h-full bg-gradient-to-r from-primary-500 to-primary-600"
          style={{ width: `${readProgress}%` }}
          initial={{ width: 0 }}
          animate={{ width: `${readProgress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50/10 pt-16">
        {/* Compact Hero Section */}
        <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 pt-8 pb-12 md:pb-16">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <LocalizedLink
              to="/blog"
              className="inline-flex items-center text-white/90 hover:text-white mb-6 transition-colors group"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              {t('blog.backToBlog')}
            </LocalizedLink>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-4">
                <SafeIcon icon={FiTag} className="mr-2 h-3.5 w-3.5" />
                {t(`blog.categories.${article.category}`)}
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                {article.title}
              </h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                <div className="flex items-center">
                  <SafeIcon icon={FiUser} className="h-4 w-4 mr-1.5" />
                  <span>{article.author}</span>
                </div>
                <span className="text-white/40">•</span>
                <div className="flex items-center">
                  <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-1.5" />
                  <span>{formatDate(article.publishDate)}</span>
                </div>
                <span className="text-white/40">•</span>
                <div className="flex items-center">
                  <SafeIcon icon={FiClock} className="h-4 w-4 mr-1.5" />
                  <span>{article.readTime} {t('blog.minutesRead')}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Article Content Container with Gradient Transition */}
        <div className="relative">
          {/* Gradient Overlay for smooth transition */}
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-primary-800/20 to-transparent pointer-events-none"></div>
          
          <article className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16 relative z-10">
            <div className="max-w-4xl mx-auto">
              {/* Hero Image - Fixed positioning to avoid overlap */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="relative mt-8 mb-12 rounded-2xl overflow-hidden shadow-2xl bg-white p-2"
              >
                <div className="relative rounded-xl overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-[280px] md:h-[420px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                </div>
              </motion.div>

              {/* Floating Action Buttons */}
              <div className="fixed right-4 md:right-8 bottom-8 z-40 flex flex-col gap-3">
                {/* Table of Contents */}
                {headings.length > 0 && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    onClick={() => setShowTOC(!showTOC)}
                    className="w-12 h-12 bg-white text-primary-600 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center group"
                    aria-label="Table of Contents"
                  >
                    <SafeIcon icon={FiList} className="h-5 w-5" />
                  </motion.button>
                )}

                {/* Share Button */}
                <motion.button
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                  onClick={handleShare}
                  className="w-12 h-12 bg-white text-primary-600 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center"
                  aria-label="Share Article"
                >
                  <SafeIcon icon={FiShare2} className="h-5 w-5" />
                </motion.button>

                {/* Scroll to Top */}
                <AnimatePresence>
                  {showScrollTop && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0 }}
                      onClick={scrollToTop}
                      className="w-12 h-12 bg-primary-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center"
                      aria-label="Scroll to Top"
                    >
                      <SafeIcon icon={FiArrowUp} className="h-5 w-5" />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>

              {/* Table of Contents Modal */}
              <AnimatePresence>
                {showTOC && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setShowTOC(false)}
                      className="fixed inset-0 bg-black/50 z-40"
                    />
                    <motion.div
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 100 }}
                      className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-50 overflow-y-auto"
                    >
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold text-gray-900">{t('blog.tableOfContents')}</h3>
                          <button
                            onClick={() => setShowTOC(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            ✕
                          </button>
                        </div>
                        <nav className="space-y-2">
                          {headings.map((heading, index) => (
                            <button
                              key={heading.id}
                              onClick={() => scrollToHeading(heading.id)}
                              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors text-sm"
                            >
                              {index + 1}. {heading.text}
                            </button>
                          ))}
                        </nav>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>

              {/* Article Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="prose prose-lg max-w-none"
              >
                <div
                  className="article-content bg-white rounded-2xl shadow-lg p-6 md:p-10
                    [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mb-6 [&_h2]:mt-12 [&_h2]:first:mt-0 [&_h2]:pb-4 [&_h2]:border-b-2 [&_h2]:border-primary-200 [&_h2]:scroll-mt-24
                    [&_h3]:text-xl [&_h3]:md:text-2xl [&_h3]:font-bold [&_h3]:text-primary-700 [&_h3]:mb-4 [&_h3]:mt-8
                    [&_p]:text-base [&_p]:md:text-lg [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:mb-5
                    [&_ul]:my-6 [&_ul]:space-y-3 [&_ul]:pl-6
                    [&_ol]:my-6 [&_ol]:space-y-3 [&_ol]:pl-6
                    [&_li]:text-base [&_li]:md:text-lg [&_li]:text-gray-700 [&_li]:leading-relaxed [&_li]:pl-2
                    [&_li::marker]:text-primary-600 [&_li::marker]:font-bold
                    [&_strong]:text-gray-900 [&_strong]:font-semibold
                    [&_em]:text-primary-600 [&_em]:font-medium [&_em]:not-italic
                    [&_blockquote]:my-8 [&_blockquote]:p-6 [&_blockquote]:md:p-8 [&_blockquote]:bg-gradient-to-br [&_blockquote]:from-primary-50 [&_blockquote]:to-primary-100/50 [&_blockquote]:border-l-4 [&_blockquote]:border-primary-500 [&_blockquote]:rounded-r-2xl [&_blockquote]:shadow-lg
                    [&_blockquote_strong]:text-primary-900 [&_blockquote_strong]:text-lg [&_blockquote_strong]:block [&_blockquote_strong]:mb-3 [&_blockquote_strong]:font-bold
                    [&_blockquote_p]:text-gray-800 [&_blockquote_p]:mb-0 [&_blockquote_p]:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: contentWithIds }}
                />
              </motion.div>

              {/* Tags Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-12 pt-8 border-t-2 border-gray-200"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700 mr-2">{t('blog.tags')}:</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-4 py-2 bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 rounded-full text-sm font-medium hover:shadow-md hover:scale-105 transition-all cursor-pointer border border-primary-200"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-16 pt-12 border-t-2 border-gray-200"
                >
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                    {t('blog.relatedArticles')}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedArticles.map((relatedArticle, index) => (
                      <motion.div
                        key={relatedArticle.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 + index * 0.1 }}
                      >
                        <LocalizedLink
                          to={`/blog/${relatedArticle.slug}`}
                          className="block group h-full"
                        >
                          <div className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-2 h-full flex flex-col">
                            <div className="relative h-48 overflow-hidden">
                              <img
                                src={relatedArticle.image}
                                alt={relatedArticle.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            </div>
                            <div className="p-5 flex-1 flex flex-col">
                              <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 text-lg group-hover:text-primary-600 transition-colors">
                                {relatedArticle.title}
                              </h3>
                              <p className="text-sm text-gray-600 line-clamp-2 mb-3 flex-1">
                                {relatedArticle.excerpt}
                              </p>
                              <div className="flex items-center text-xs text-gray-500 pt-3 border-t border-gray-100">
                                <SafeIcon icon={FiClock} className="h-3.5 w-3.5 mr-1" />
                                <span>{relatedArticle.readTime} {t('blog.minutesRead')}</span>
                              </div>
                            </div>
                          </div>
                        </LocalizedLink>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </article>
        </div>
      </div>
    </>
  );
};

export default BlogArticlePage;